import {
  ColumnFactory,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  FeatureFlags,
  FeatureFlagsContext,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  TableauUser,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { WorkbooksDisplayGridRowDataFragment } from '../../../gen-src/queries/WorkbooksDisplayGridRowDataFragment';
import { WorkbooksDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/WorkbooksDisplayGridRowDataFragmentWithoutPersonalSpace';
import { WorkbookOrderField } from '../../../gen-src/queries/globalTypes';
import { createLinkOrTextCellFormatter } from '../datagrid/cellFormatters/LinkCellFormatter';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import { createObfuscatedLinkCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import { WorkbooksValidSortFields } from '../sorting/sortProviders/WorkbooksGridSortProvider';

const shouldBeObfuscated: (rowData: WorkbooksDisplayGridRowData) => boolean = ({ name }) => name === null;
const projectContainerType = 'project';

export interface WorkbooksDisplayGridRowData {
  readonly id: string;
  readonly name: string | null;
  readonly vizportalUrlId: string;
  readonly owner: TableauUser;
  readonly sheetCount: number | null;
  readonly containerName: string | null;
  readonly containerType: string;
  readonly projectVizportalUrlId: string | null;
}

export function getRowDataForWorkbookWithoutPersonalSpace(
  workbook: WorkbooksDisplayGridRowDataFragmentWithoutPersonalSpace
): WorkbooksDisplayGridRowData {
  return {
    id: workbook.id,
    name: workbook.name,
    vizportalUrlId: workbook.vizportalUrlId,
    owner: workbook.owner,
    sheetCount: workbook.sheetsConnection ? workbook.sheetsConnection.totalCount : null,
    containerName: workbook.projectName,
    containerType: projectContainerType,
    projectVizportalUrlId: workbook.projectVizportalUrlId,
  };
}

export function getRowDataForWorkbook(workbook: WorkbooksDisplayGridRowDataFragment): WorkbooksDisplayGridRowData {
  return {
    id: workbook.id,
    name: workbook.name,
    vizportalUrlId: workbook.vizportalUrlId,
    owner: workbook.owner,
    sheetCount: workbook.sheetsConnection ? workbook.sheetsConnection.totalCount : null,
    containerName: workbook.containerName,
    containerType: workbook.containerType,
    projectVizportalUrlId: workbook.projectVizportalUrlId,
  };
}

type WorkbooksColumnFactory = ColumnFactory<GridSortProps<WorkbooksValidSortFields>, WorkbooksDisplayGridRowData, WorkbooksValidSortFields>;

function columns(featureFlags: FeatureFlags): ReadonlyArray<WorkbooksColumnFactory> {
  const cols: ReadonlyArray<WorkbooksColumnFactory> = [
    (sortParams) => ({
      name: 'name',
      dataKey: WorkbookOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { vizportalUrlId }) => urls.workbookLineagePath(vizportalUrlId)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'sheets',
      dataKey: WorkbookOrderField.SHEETS_COUNT,
      headerText: Messages.GridHeaderSheets(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter<WorkbooksDisplayGridRowData>(({ sheetCount }) => sheetCount),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: featureFlags.personalSpaceEnabled ? 'location' : 'project',
      dataKey: WorkbookOrderField.PROJECT_NAME,
      headerText: featureFlags.personalSpaceEnabled ? Messages.GridHeaderLocation() : Messages.GridHeaderProject(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkOrTextCellFormatter(
        ({ containerType, containerName }) => {
          return containerType === projectContainerType ? containerName : Messages.PersonalSpaceName();
        },
        (urls, { projectVizportalUrlId, containerType }) => (projectVizportalUrlId && containerType === projectContainerType ? urls.projectPath(projectVizportalUrlId) : null)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'owner',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderOwner(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createUserNameLinkCellFormatter(({ owner }) => owner),
      resizable: true,
      sortable: false,
    }),
  ];

  return cols.filter(Boolean) as ReadonlyArray<WorkbooksColumnFactory>;
}

export type WorkbooksDisplayGridProps = UsualDisplayGridProps<WorkbooksDisplayGridRowData, GraphQLError> &
  GridSortProps<WorkbooksValidSortFields>;

export function WorkbooksDisplayGrid(props: WorkbooksDisplayGridProps): JSX.Element {
  const featureFlags = React.useContext(FeatureFlagsContext);

  const cols = columns(featureFlags).map((columnFactory) => columnFactory(props));
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
